<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaSaveState from '@/components/design/composite/GaiaSaveState.vue';
import GaiaSigningActivateStep from '@/components/design/signing/GaiaSigningActivateStep.vue';
import GaiaSigningDocumentsStep from '@/components/design/signing/GaiaSigningDocumentsStep.vue';
import GaiaSigningFieldsStep from '@/components/design/signing/GaiaSigningFieldsStep.vue';
import GaiaSigningInitStep from '@/components/design/signing/GaiaSigningInitStep.vue';
import GaiaSigningRecipientsStep from '@/components/design/signing/GaiaSigningRecipientsStep.vue';
import GaiaStepFlow from '@/components/design/composite/GaiaStepFlow.vue';
import { type GaiaStepRailItem } from '@/components/design/composite/GaiaStepRail.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaWizardFooter from '@/components/design/composite/GaiaWizardFooter.vue';
import WizardLayout from '@/components/design/layout/WizardLayout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import SignatureFlowDebugPanel from '@/components/contractManagement/signatureFlow/SignatureFlowDebugPanel.vue';
import { SIGNING_WIZARD_STEPS, useSigningWizard } from '@/composables/useSigningWizard';
import type { SignatureFlowStepKey } from '@/types/contractManagement/signatureFlow';
import { NFlex, useDialog } from 'naive-ui';
import { signingRequestRoute } from '@/router/links';

/**
 * The rebuilt prepare-signature wizard (Signing — Create Wizard mock): the
 * step rail on the left, the active step in the middle, a pinned action bar
 * below. The state machine lives in `useSigningWizard`; this view is the shell
 * and the step switch.
 */

const route = useRoute();
const wizardApi = useSigningWizard();
const {
  wizard,
  activeStep,
  activeStepDefinition,
  isEditMode,
  contractUid,
  reviewParties,
  activateRequestPreview,
  signatureClearAcknowledgements,
  revisionsRequiringClearAck,
  documentsWithSupersededRevision,
  adoptDocumentRevision,
  allSignableDocumentsDetected,
  initializeNameError,
  initializeExpirationError,
  saveState,
  saveError,
  lastSavedAt,
  flushSaves,
  submitting,
  continueLabel,
  continueDisabled,
  canGoBack,
  activeStepBlocker,
  isStepAccessible,
  isStepComplete,
  stepCount,
  selectStep,
  goBack,
  goNext,
  updateInitializeForm,
  updateUploadForm,
  updateSigneesForm,
  updateReviewForm,
} = wizardApi;

const dialog = useDialog();

/** The footer shows the draft's save state once there is a draft (or its first save is under way). */
const showSaveState = computed(
  () => wizard.signingRequestUid !== null || saveState.value !== 'saved'
);
const lastSavedLabel = computed(() =>
  lastSavedAt.value
    ? `at ${lastSavedAt.value.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
    : ''
);

/**
 * The draft is on the server, so leaving is always fine — once what is pending
 * has been sent. Only a save that keeps failing is worth a prompt.
 *
 * A router guard rather than `onBeforeRouteLeave`: the first save moves a new
 * request onto its edit route while this view stays mounted, and a leave guard
 * stays bound to the route it was registered on. Moves within the same draft
 * (that one, and `?step=`) pass straight through — the first is made *by* a
 * save, which waiting on here would deadlock.
 */
const router = useRouter();
const removeLeaveGuard = router.beforeEach(async (to, from) => {
  const sameView =
    to.name === from.name && to.params.signingRequestUid === from.params.signingRequestUid;
  const toOwnDraft =
    to.name === 'signing-request-edit' && to.params.signingRequestUid === wizard.signingRequestUid;
  if (sameView || toOwnDraft || saveState.value === 'saved') return true;
  await flushSaves();
  if (saveState.value !== 'error') return true;
  return await new Promise<boolean>(resolve => {
    dialog.warning({
      title: 'Leave without saving?',
      content:
        "Your latest changes couldn't be saved. If you leave now they will be lost. You can stay and try saving again.",
      positiveText: 'Leave anyway',
      negativeText: 'Stay',
      onPositiveClick: () => {
        resolve(true);
      },
      onNegativeClick: () => {
        resolve(false);
      },
      onClose: () => {
        resolve(false);
      },
    });
  });
});
onBeforeUnmount(removeLeaveGuard);

/**
 * A step's misses are only worth showing once the user has tried to leave it —
 * an untouched step shouldn't greet them in red.
 */
const touchedSteps = ref<Set<SignatureFlowStepKey>>(new Set());

const showBlocker = computed(
  () => activeStepBlocker.value !== null && touchedSteps.value.has(activeStep.value)
);

// Arriving on a step clears nothing, but leaving one marks it: `goNext` refuses
// while the gate is closed, and that refusal is what should surface the reason.
async function handleNext(): Promise<void> {
  touchedSteps.value = new Set([...touchedSteps.value, activeStep.value]);
  await goNext();
}

watch(activeStep, () => {
  // Re-entering a step the user already tried keeps its blocker visible; a
  // fresh step starts quiet.
  if (!activeStepBlocker.value) {
    const next = new Set(touchedSteps.value);
    next.delete(activeStep.value);
    touchedSteps.value = next;
  }
});

const railItems = computed<GaiaStepRailItem[]>(() =>
  SIGNING_WIZARD_STEPS.map(step => ({
    key: step.key,
    label: step.title,
    number: step.number,
    description: step.hint,
    count: stepCount(step.key),
    done: isStepComplete(step.key) && step.key !== activeStep.value,
    locked: !isStepAccessible(step.key),
  }))
);

const footerNote = computed(() => {
  if (activeStepBlocker.value) {
    return `Complete this step to continue: ${activeStepBlocker.value}`;
  }

  const index = SIGNING_WIZARD_STEPS.findIndex(step => step.key === activeStep.value);
  const next = SIGNING_WIZARD_STEPS.at(index + 1);

  return next
    ? `Step ${index + 1} of ${SIGNING_WIZARD_STEPS.length} complete — next: ${next.title}`
    : 'Recipients are notified as soon as you send it.';
});

const title = computed(() => (isEditMode.value ? 'Edit signature draft' : 'Prepare signature'));

/**
 * In edit mode the trail passes through the request being edited, so the crumb
 * can lead back to its record. The name is the one hydrated into the form.
 */
const editingSigningRequestUid = computed(() =>
  typeof route.params.signingRequestUid === 'string' && route.params.signingRequestUid.length > 0
    ? route.params.signingRequestUid
    : null
);
const editingRequestLabel = computed(() => wizard.initializeForm.name.trim() || 'Request');
</script>

<template>
  <!-- Signature fields is a workspace — the document beside its sidebar, each
       scrolling in its own pane — so that step takes the column in fill mode. -->
  <!-- Placing fields is a workspace: the document takes the column uncapped and
       full-height, with a tighter inset, as the drafting wizard's compose step. -->
  <wizard-layout
    :fill="activeStep === 'review'"
    :content-max-width="activeStep === 'review' ? 'none' : undefined"
    :content-padding="activeStep === 'review' ? '14px 24px 18px' : undefined"
  >
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.sign }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'signing-requests' }">
          {{ navLabels.signatureRequests }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item v-if="editingSigningRequestUid" clickable>
        <router-link :to="signingRequestRoute(editingSigningRequestUid)">
          {{ editingRequestLabel }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ title }}</n-breadcrumb-item>
    </template>
    <template #rail>
      <n-flex vertical :size="14" :wrap="false" class="gaia-wizard-rail">
        <n-flex vertical :size="4" :wrap="false">
          <gaia-header :level="1" size="page">{{ title }}</gaia-header>
          <gaia-text
            class="gaia-wizard-rail__hint"
            size="meta"
            tone="subtle"
            :style="{ lineHeight: 1.5 }"
          >
            Start with the documents. Steps unlock as you go, and stay editable until you send.
          </gaia-text>
        </n-flex>
        <gaia-step-flow
          data-tour="signing.steps"
          :items="railItems"
          :active="activeStep"
          @select="key => selectStep(key as SignatureFlowStepKey)"
        />
      </n-flex>
    </template>

    <n-flex
      vertical
      :size="18"
      :wrap="false"
      :style="activeStep === 'review' ? { flex: 1, minHeight: 0 } : undefined"
    >
      <n-flex vertical :size="4" :wrap="false">
        <gaia-header :level="2">{{ activeStepDefinition.heading }}</gaia-header>
        <gaia-text size="body" tone="tertiary">{{ activeStepDefinition.hint }}</gaia-text>
      </n-flex>

      <gaia-banner v-if="showBlocker" type="warning">{{ activeStepBlocker }}</gaia-banner>
      <gaia-banner v-if="saveState === 'error' && saveError" type="error">
        Your draft couldn’t be saved: {{ saveError }}
      </gaia-banner>

      <!-- What is signed on the left; the request built around it on the right.
           Its name follows the documents until it is typed over. -->
      <gaia-signing-documents-step
        v-if="activeStep === 'upload'"
        :form="wizard.uploadForm"
        :provider="wizard.initializeForm.provider"
        :current-contract-uid="contractUid"
        @update:form="updateUploadForm"
      >
        <template #aside>
          <gaia-signing-init-step
            :form="wizard.initializeForm"
            :name-content-error="initializeNameError"
            :expiration-error="initializeExpirationError"
            :provider-locked="isEditMode"
            :touched="touchedSteps.has('upload')"
            @update:form="updateInitializeForm"
          />
        </template>
      </gaia-signing-documents-step>

      <gaia-signing-recipients-step
        v-else-if="activeStep === 'signees'"
        :form="wizard.signeesForm"
        :ordered-signers="wizard.initializeForm.ordered_signers"
        :ordered-approvers="wizard.initializeForm.ordered_approvers"
        @update:form="updateSigneesForm"
        @update:ordered-signers="
          value => updateInitializeForm({ ...wizard.initializeForm, ordered_signers: value })
        "
        @update:ordered-approvers="
          value => updateInitializeForm({ ...wizard.initializeForm, ordered_approvers: value })
        "
      />

      <gaia-signing-fields-step
        v-else-if="activeStep === 'review'"
        :form="wizard.reviewForm"
        :parties="reviewParties"
        :documents="wizard.uploadForm.documents"
        :ordered-signers="wizard.initializeForm.ordered_signers"
        @update:form="updateReviewForm"
      />

      <gaia-signing-activate-step
        v-else
        :request-preview="activateRequestPreview"
        :parties="reviewParties"
        :revisions-requiring-ack="revisionsRequiringClearAck"
        :acknowledgements="signatureClearAcknowledgements"
        :detection-complete="allSignableDocumentsDetected"
        :error="wizard.steps.activate.error"
        :superseded-documents="documentsWithSupersededRevision"
        :message="wizard.initializeForm.message"
        @update:acknowledgements="value => (signatureClearAcknowledgements = value)"
        @go-to-step="selectStep"
        @update:message="
          (value: string) => updateInitializeForm({ ...wizard.initializeForm, message: value })
        "
        @adopt-document-revision="adoptDocumentRevision"
      />

      <signature-flow-debug-panel
        :state="wizard.steps[activeStep]"
        :request-preview="activateRequestPreview"
      />
    </n-flex>

    <template #footer>
      <gaia-wizard-footer
        :back-attrs="{ 'data-tour': 'signing.wizard-back' }"
        :next-attrs="{ 'data-tour': 'signing.continue' }"
        :back-label="canGoBack ? 'Back' : undefined"
        :next-label="continueLabel"
        :loading="submitting"
        :next-disabled="continueDisabled"
        @back="goBack"
        @next="handleNext"
      >
        <!-- The draft's save state, then where the wizard stands. -->
        <template #note>
          <n-flex align="center" :size="12" :wrap="false" :style="{ minWidth: 0 }">
            <n-flex v-if="showSaveState" align="center" :size="6" :wrap="false" style="flex: none">
              <gaia-save-state :state="saveState" @save="flushSaves" />
              <gaia-text v-if="saveState === 'saved' && lastSavedLabel" size="micro" tone="subtle">
                {{ lastSavedLabel }}
              </gaia-text>
            </n-flex>
            <gaia-text truncate size="control" tone="subtle">{{ footerNote }}</gaia-text>
          </n-flex>
        </template>
        <!-- While Continue is off, hovering it says what is still missing — the
             same sentence as the blocker banner, from the same gate. -->
        <template v-if="continueDisabled && activeStepBlocker" #next-tooltip>
          {{ activeStepBlocker }}
        </template>
      </gaia-wizard-footer>
    </template>
  </wizard-layout>
</template>
